<template>
  <div>
    <b-row>
      <b-col class="d-flex justify-content-between pb-1 align-items-center">
        <h1 class="font-weight-bolder">
          {{ $t('allQuotes') }}
        </h1>
      </b-col>
    </b-row>

    <div class="d-flex flex-wrap">
      <b-form-group
        :label="$t('quoteFolio')"
        class="mr-1"
      >
        <b-input-group>
          <b-form-input
            v-model.trim="quoteFolio"
            placeholder="F1234"
            @keyup.enter="applyFilters"
          />
          <b-input-group-append>
            <b-button
              variant="outline-primary"
              @click="applyFilters"
            >
              {{ $t('search') || 'Buscar' }}
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-form-group
        :label="$t('paymentStatus')"
        class="mr-1"
      >
        <b-form-select
          v-model="paymentStatus"
          :options="statusOptions"
          @change="handleFetchQuotes"
        />
      </b-form-group>

      <b-form-group
        :label="$t('customer')"
        class="mr-1"
      >
        <b-form-radio-group
          v-model="customerInvoiced"
          buttons
          button-variant="outline-primary"
          @change="handleFetchQuotes"
        >
          <b-form-radio :value="null">
            {{ $t('all') }}
          </b-form-radio>
          <b-form-radio :value="true">
            {{ $t('invoiced') }}
          </b-form-radio>
          <b-form-radio :value="false">
            {{ $t('pending') }}
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group
        :label="$t('supplier')"
        class="mr-1"
      >
        <b-form-radio-group
          v-model="supplierInvoiced"
          buttons
          button-variant="outline-primary"
          @change="handleFetchQuotes"
        >
          <b-form-radio :value="null">
            {{ $t('all') }}
          </b-form-radio>
          <b-form-radio :value="true">
            {{ $t('invoiced') }}
          </b-form-radio>
          <b-form-radio :value="false">
            {{ $t('pending') }}
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>
    </div>

    <b-row v-if="quotes.length > 0">
      <b-col>
        <b-overlay :show="loading">
          <quotes-table
            :quotes="quotes"
            :allow-delete="true"
            :allow-restore="true"
            :show-created-to-closed-duration="true"
            :show-visibility="true"
            @refetch="handleFetchQuotes"
          />
        </b-overlay>
      </b-col>

      <b-col sm="12">
        <b-row>
          <b-col class="d-flex justify-content-end">
            <b-pagination
              :value="quotesPagination.page"
              :total-rows="quotesPagination.total_objects"
              :per-page="quotesPagination.per_page"
              aria-controls="my-table"
              @change="nextPage($event)"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row v-else>
      <b-col>
        <h4 class="display-4">
          No hay Cotizaciones 🧟
        </h4>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import QuotesTable from '@/components/purchaseOrders/PurchaseOrdersViewTable.vue'

export default {
  components: {
    QuotesTable,
  },

  data() {
    return {
      loading: false,

      // TODAS por default (sin filtro de pago)
      paymentStatus: null,

      customerInvoiced: null,
      supplierInvoiced: null,
      quoteFolio: '',

      // para conservar sort cuando cambias página
      lastSortCtx: null,

      statusOptions: [
        { text: 'Todas', value: null }, // <- importante: NULL = sin filtro
        { text: this.$t('awaiting_payment_approval'), value: 'awaiting_payment_approval' },
        { text: this.$t('payment_approved'), value: 'payment_approved' },
        { text: this.$t('payment_rejected'), value: 'payment_rejected' },
        { text: this.$t('credit_approved'), value: 'credit_approved' },
      ],
    }
  },

  computed: {
    ...mapGetters('quotes', ['quotes', 'quotesPagination']),
  },

  created() {
    this.handleFetchQuotes()
  },

  methods: {
    ...mapMutations('quotes', {
      clearQuotes: 'CLEAR_QUOTES',
      changePagination: 'CHANGE_PAGINATION',
    }),
    ...mapActions('quotes', ['fetchQuotes']),

    normalizedQuoteFolio() {
      return this.quoteFolio.toString().trim()
    },

    applyFilters() {
      this.changePagination({ field: 'page', value: 1 })
      this.handleFetchQuotes(this.lastSortCtx)
    },

    handleFetchQuotes(sortCtx = null) {
      this.loading = true
      if (sortCtx) this.lastSortCtx = sortCtx

      const { page, per_page } = this.quotesPagination

      // sort backend: "campo:asc|desc"
      const effectiveSort = sortCtx || this.lastSortCtx
      const sortParam = effectiveSort?.sortBy
        ? `${effectiveSort.sortBy}:${effectiveSort.sortDesc ? 'desc' : 'asc'}`
        : null

      const baseParams = {
        // mantener por compatibilidad:
        page,
        per_page,

        // PAginacion
        meta: { pagination: { page, per_page } },

        // filtros de facturación
        by_is_invoiced: this.customerInvoiced,
        by_supplier_is_invoiced: this.supplierInvoiced,
        ...(this.normalizedQuoteFolio() ? { by_folio: this.normalizedQuoteFolio() } : {}),

        include_deleted: true,

        ...(sortParam ? { sort: sortParam } : {}),
      }

      // 1) TODAS -> no mandes filtro de pago
      if (!this.paymentStatus) {
        this.fetchQuotes(baseParams)
          .finally(() => { this.loading = false })
        return
      }

      // 2) Status de pago específico
      this.fetchQuotes({
        ...baseParams,
        by_payment_status: this.paymentStatus,
      }).finally(() => {
        this.loading = false
      })
    },

    nextPage(value) {
      this.changePagination({ field: 'page', value })
      this.handleFetchQuotes(this.lastSortCtx)
    },
  },
}
</script>

<style></style>
